// BL-2028 — the ledger's routes (ADR 065 — Money Model § 8). An entry hangs off
// an invoice (receivable) or, since BL-2509, a settlement payout line (payable);
// there is no PATCH or DELETE anywhere (append-only).
import { responseType } from '@auctogon/core/api/registry';
import { defineAdminRoute, type PolymorphicEntityGrant } from './define-admin-route';
// Type-only import (erased at build) — keeps the registry client-bundle-safe.
import type { InvoiceLedgerDto, LedgerEntryDetailDto, LedgerEntryDto } from '../../schemas/responses/ledger-entry';
import type {
  AchPaymentResultDto,
  CardPaymentPriceDto,
  CardPaymentQuoteDto,
  CardPaymentResultDto,
  PaymentReversalResultDto,
  ReceivePaymentOptionsDto,
} from '../../schemas/responses/card-payment';

/** The invoice the entries belong to; another house's (or a missing) invoice is the gate's 404. */
const INVOICE_RECORD = { from: 'record', table: 'invoice', param: 'id' } as const;

/** The entry itself; another house's (or a missing) entry is the gate's 404. */
const LEDGER_ENTRY_RECORD = { from: 'record', table: 'ledger_entry', param: 'id' } as const;

/**
 * BL-2509 — an entry's permission is its record's (ADR 062 — Access Grants,
 * `grantBy`): an invoice payment needs the invoice grant, a settlement payout
 * the settlement one. The gate reads `direction` off the loaded entry, so a
 * settlement-only role can never act on an invoice payment, or the reverse.
 */
const ENTRY_READ = {
  receivable: 'invoice.read',
  payable: 'settlement.read',
} as const satisfies Record<string, PolymorphicEntityGrant>;

/**
 * As {@link ENTRY_READ}, for a pending entry's one-time outcome stamp.
 *
 * BUG-2181 G5 (deliberate, not a drift from `payment.create`): posting the
 * payment or payout is the money-moving act, gated under `payment.create`
 * (BL-2374) since M3. The outcome stamp runs later, when the processor
 * confirms or fails a pending entry that was already posted — no money
 * moves here, and the invoice/settlement already carries the pending entry —
 * so it is the record's own write, gated like every other invoice/settlement
 * update. `payment-grant-pairings.test.ts` pins the pairing so a future role
 * change can't silently split who may post from who may confirm.
 */
const ENTRY_UPDATE = {
  receivable: 'invoice.update',
  payable: 'settlement.update',
} as const satisfies Record<string, PolymorphicEntityGrant>;

export const ledger_entries = {
  invoice: defineAdminRoute({
    path: '/api/invoices/:id/ledger-entries',
    params: { id: '' },
    methods: {
      // Every entry of the invoice, and its paid as of `asOf` (the payment date) when given.
      GET: {
        grant: 'invoice.read',
        tenant: INVOICE_RECORD,
        response: responseType<InvoiceLedgerDto>(),
        query: { asOf: '' },
      },
      // Post a payment, refund, chargeback or payment reversal. Moving money needs
      // payments.manage (M3; BL-2374 re-gated it from invoice.update, rule #23 sign-off Oct 8).
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<LedgerEntryDto>() },
    },
  }),
  // BL-2379 (PAY-1b): charge one of the buyer's saved cards for the invoice.
  // Moving money needs payments.manage (M3), like recording a payment by hand.
  cardPayments: defineAdminRoute({
    path: '/api/invoices/:id/card-payments',
    params: { id: '' },
    methods: {
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<CardPaymentResultDto>() },
    },
  }),
  // BL-2745: read a card typed in now at the processor (its funding decides the
  // card fee) before it is charged. Sets the card up there, so it is gated like the charge.
  cardPaymentQuote: defineAdminRoute({
    path: '/api/invoices/:id/card-payments/quote',
    params: { id: '' },
    methods: {
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<CardPaymentQuoteDto>() },
    },
  }),
  // BL-2869: the server's price of a card charge (fee, tax on the fee, total). Read-only, but gated
  // as the charge it prices (ADR 062 — Access Grants): the figure is the house's fee settings.
  cardPaymentPrice: defineAdminRoute({
    path: '/api/invoices/:id/card-payments/price',
    params: { id: '' },
    methods: {
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<CardPaymentPriceDto>() },
    },
  }),
  // BL-2385 (PAY-1c): staff debit a buyer's saved, verified bank account (ACH),
  // recording the buyer's authorization. Moving money needs payments.manage (M3).
  // BL-2573 (PAY-1c·2): or a bank account the buyer gives now, saved on their file first.
  achPayments: defineAdminRoute({
    path: '/api/invoices/:id/ach-payments',
    params: { id: '' },
    methods: {
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<AchPaymentResultDto>() },
    },
  }),
  // BL-2387 (PAY-5b): Reverse… on a payment. A card or ACH payment goes back
  // through the processor; a manual one records the undo. Moving money needs
  // payments.manage (M3), like taking it.
  paymentReversals: defineAdminRoute({
    path: '/api/invoices/:id/payment-reversals',
    params: { id: '' },
    methods: {
      POST: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<PaymentReversalResultDto>() },
    },
  }),
  // BL-2379 / BL-2385: what Receive payment's Card and ACH methods read (limits,
  // fee rate, saved cards and bank accounts, and the secure form for a new card or
  // bank account, BL-2460 / BL-2573). Only those who may take a payment read it.
  paymentOptions: defineAdminRoute({
    path: '/api/invoices/:id/payment-options',
    params: { id: '' },
    methods: {
      GET: { grant: 'payment.create', tenant: INVOICE_RECORD, response: responseType<ReceivePaymentOptionsDto>() },
    },
  }),
  detail: defineAdminRoute({
    path: '/api/ledger-entries/:id',
    params: { id: '' },
    // GET only: an entry is never edited or deleted, so PATCH / DELETE are Next's 405.
    methods: {
      GET: {
        grant: { none: "per entity — the entry's direction picks the grant, see tenant.grantBy" },
        tenant: { ...LEDGER_ENTRY_RECORD, grantBy: { column: 'direction', grants: ENTRY_READ } },
        response: responseType<LedgerEntryDetailDto>(),
      },
    },
  }),
  outcome: defineAdminRoute({
    path: '/api/ledger-entries/:id/outcome',
    params: { id: '' },
    methods: {
      // A pending entry's one-time settled / failed stamp (MN6): an invoice payment's or a payout's (BL-2509).
      POST: {
        grant: { none: "per entity — the entry's direction picks the grant, see tenant.grantBy" },
        tenant: { ...LEDGER_ENTRY_RECORD, grantBy: { column: 'direction', grants: ENTRY_UPDATE } },
        response: responseType<LedgerEntryDto>(),
      },
    },
  }),
};
